import { useState } from 'react';
import { ContentBox } from '@/components/shared/content-box';
import { Typography } from '@mui/material';
import {
  ToggleButton,
  ToggleButtonGroup,
  ToggleContent,
} from '@/components/shared/toggle-button-group';
import { ConfirmReplaceFeedbackDialog } from './components/confirm-replace-feedback-dialog';
import { useWizard } from '@/context/wizard';
import { useUpdateProfileBasedRecommendation } from '@/data/idp/profile-based-recommendations';
import { CreatePlanWizardValues } from '@/packages/idp/pages/create-plan/types';
import { RecommendationPlanIds } from '@/data/idp/profile-based-recommendations/interfaces';
import { FeedbackOptions } from '@/packages/idp/pages/create-plan/types';
import { StepActions } from '@/packages/idp/pages/create-plan/components/step-actions';

export const Feedback = () => {
  const { actions, values } = useWizard<CreatePlanWizardValues>();
  const { mutate: updateRecommendation } = useUpdateProfileBasedRecommendation(
    values?.id || ''
  );
  const value = values?.[RecommendationPlanIds.Feedback];
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const navigateToNewFeedback = () => {
    updateRecommendation(
      { current_step: RecommendationPlanIds.NewFeedback },
      {
        onSuccess: () => {
          actions.navigate(RecommendationPlanIds.NewFeedback);
        },
      }
    );
  };

  const handleNext = () => {
    if (value?.option === FeedbackOptions.New) {
      setIsConfirmDialogOpen(true);
      return;
    }

    const feedbackId = value?.currentFeedback?.id;
    if (!feedbackId) return;

    updateRecommendation(
      {
        current_step: RecommendationPlanIds.SkillSelection,
        plan: { feedback_ids: [feedbackId] },
      },
      {
        onSuccess: () => {
          actions.updateValues({
            [RecommendationPlanIds.Feedback]: {
              ...value,
              option: value?.option || null,
            },
          });
          actions.navigate(RecommendationPlanIds.SkillSelection);
        },
      }
    );
  };

  const handleSkipFeedback = () => {
    updateRecommendation(
      { current_step: RecommendationPlanIds.SkillSelection },
      {
        onSuccess: () => {
          actions.updateValues({
            [RecommendationPlanIds.Feedback]: {
              ...value,
              option: FeedbackOptions.Skip,
            },
          });
          actions.navigate(RecommendationPlanIds.SkillSelection);
        },
      }
    );
  };

  const handleBack = () => {
    actions.navigate(RecommendationPlanIds.Duration);
  };

  return (
    <ContentBox>
      <Typography variant="h2">Feedback ou avaliação</Typography>
      <Typography mb={3} mt={1}>
        Seus feedbacks ajudam a IA a entender melhor suas forças e
        oportunidades. Usar esse histórico é a melhor forma de deixar seu plano
        ainda mais estratégico e conectado com o seu momento atual.
      </Typography>
      <ToggleButtonGroup
        value={value?.option}
        onChange={(newValue) => {
          actions.updateValues({
            [RecommendationPlanIds.Feedback]: {
              ...value,
              option: newValue,
            },
          });
        }}
        aria-label="Escolha entre o feedback atual ou adicionar um novo."
      >
        <ToggleButton value={FeedbackOptions.Current}>
          <ToggleContent
            icon="chat-like"
            title="Usar feedback atual"
            description={value?.currentFeedback?.generated_summary}
          />
        </ToggleButton>
        <ToggleButton value={FeedbackOptions.New}>
          <ToggleContent
            icon="feedback-replace"
            title="Substituir feedback"
            description="Um novo feedback substituirá o que está salvo na plataforma."
          />
        </ToggleButton>
      </ToggleButtonGroup>
      <StepActions
        onBack={handleBack}
        onSkip={handleSkipFeedback}
        skipLabel="Não usar feedback"
        onNext={handleNext}
        nextDisabled={!value?.option}
      />
      <ConfirmReplaceFeedbackDialog
        open={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={navigateToNewFeedback}
      />
    </ContentBox>
  );
};
